import { createSelector } from 'reselect';

import staticSearchConfig from 'store/railsBuilder/utils/staticSearchConfig';
import {
    selectIfKidsProfileActive,
    selectIsNonInteractiveUI,
} from 'store/user/selectors';

import { isWeb } from 'utils/responsiveness';

import { ReduxStore } from '../types';
import { RequestStatus } from '../utils';
import {
    MenuConfigurationItem,
    MenuItemType,
    RBParsedRail,
    RBTemplates,
    RBTemplateview,
    TemplateviewsById,
} from './reducer.types';
import {
    FALLBACK_KIDS_SETTINGS_CONFIG,
    FALLBACK_SETTINGS_CONFIG,
    FALLBACK_TEMPLATEVIEWS,
    NON_INTERACTIVE_EXCLUDED_MENU_ITEMS,
    SUPPORTED_TEMPLATEVIEWS_NAMES,
} from './utils/constants';

function selectStandardMenu(store: ReduxStore) {
    return store.railsBuilder.getMainMenuConfigRequest.standardMenu;
}

function selectKidsMenu(store: ReduxStore) {
    return store.railsBuilder.getMainMenuConfigRequest.kidsMenu;
}

export function selectMainMenuConfigRequestStatus(store: ReduxStore) {
    return store.railsBuilder.getMainMenuConfigRequest.status;
}

export const selectMainMenu = createSelector(
    selectMainMenuConfigRequestStatus,
    selectStandardMenu,
    selectKidsMenu,
    selectIfKidsProfileActive,
    (status, standardMenu, kidsMenu, isKidsProfileActive) => {
        if (status === RequestStatus.FAILURE) {
            return FALLBACK_TEMPLATEVIEWS;
        }

        return isKidsProfileActive ? kidsMenu : standardMenu;
    },
);

export const selectNavigationMenu = createSelector(selectMainMenu, (menu) =>
    menu
        .filter(({ name }) => SUPPORTED_TEMPLATEVIEWS_NAMES.includes(name))
        .slice(0, 5),
);

export const selectMainMenuItemsById = createSelector(
    selectNavigationMenu,
    (menu) =>
        menu.reduce<Record<MenuConfigurationItem['id'], MenuConfigurationItem>>(
            (acc, menuItem) => ({ ...acc, [menuItem.id]: menuItem }),
            {},
        ),
);

export const selectEpgTemplateId = createSelector(
    selectNavigationMenu,
    (menu) => menu.find(({ type }) => type === MenuItemType.EPG)?.id,
);

export const selectSettingsPageConfig = createSelector(
    selectMainMenuConfigRequestStatus,
    selectMainMenu,
    selectIsNonInteractiveUI,
    selectIfKidsProfileActive,
    (status, menu, isNonInteractiveUI, kidsProfileActive) => {
        const settingsMenuConfig = menu.find(
            ({ type }) => type === MenuItemType.SETTINGS,
        )?.children;
        const filteredMenuConfig = isNonInteractiveUI
            ? settingsMenuConfig?.filter(
                  (item) =>
                      !NON_INTERACTIVE_EXCLUDED_MENU_ITEMS.includes(
                          item.name,
                      ) ||
                      (item.name === RBTemplates.InternetConnection && !isWeb),
              )
            : settingsMenuConfig?.filter(
                  (item) =>
                      !(
                          item.name === RBTemplates.InternetConnection && isWeb
                      ) && !(item.name === RBTemplates.EpgDisplay && isWeb),
              );

        if (status === RequestStatus.FAILURE) {
            if (kidsProfileActive) {
                return FALLBACK_KIDS_SETTINGS_CONFIG;
            }

            return FALLBACK_SETTINGS_CONFIG;
        }

        return filteredMenuConfig;
    },
);

export const selectSearchMenuConfig = createSelector(selectMainMenu, (menu) =>
    menu.find(({ type }) => type === MenuItemType.SEARCH),
);

export function selectTemplateviewsById(store: ReduxStore) {
    return store.railsBuilder.templateviewsById;
}

export const selectRBTemplateviewRequestStatus = (
    id: keyof TemplateviewsById,
) =>
    createSelector(
        selectTemplateviewsById,
        (templateviewsById) =>
            templateviewsById[id]?.status || RequestStatus.IDLE,
    );

export const selectTemplateviewConfigByName = createSelector(
    [
        selectTemplateviewsById,
        (
            state: ReduxStore,
            templateviewId: keyof TemplateviewsById | undefined,
        ) => templateviewId,
    ],
    (templateviewsById, templateviewId) => {
        if (templateviewId && templateviewsById[templateviewId]?.data) {
            const templateviewSummary = templateviewsById[templateviewId].data;

            if (templateviewSummary?.rails) {
                return {
                    ...templateviewSummary,
                    rails: templateviewSummary.rails.filter(
                        ({ properties: { visible } }) =>
                            visible && JSON.parse(visible),
                    ),
                } as RBTemplateview<RBParsedRail[]>;
            }
        }

        return undefined;
    },
);

export const selectTemplateviewRailById = (
    templateviewId: keyof TemplateviewsById | undefined,
    railId: string | undefined,
) =>
    createSelector(
        (state: ReduxStore) =>
            selectTemplateviewConfigByName(state, templateviewId),
        (templateviewConfig) => {
            return templateviewConfig?.rails.find(({ id }) => id === railId);
        },
    );

export const selectSearchPageConfig = createSelector(
    selectSearchMenuConfig,
    selectTemplateviewsById,
    (searchMenu, templateviewsById) => {
        if (searchMenu?.id && templateviewsById[searchMenu.id]?.data) {
            // TODO For now we're using static search screen configuration.
            return {
                ...templateviewsById[searchMenu.id].data,
                rails: staticSearchConfig.rails,
            };
        }

        return undefined;
    },
);

export const selectSearchRailById = (railId: string | undefined) =>
    createSelector(selectSearchPageConfig, (searchPageConfig) =>
        searchPageConfig?.rails?.find(({ id }) => id === railId),
    );
